import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, FileUp, X, Loader2, CheckCircle2, AlertCircle, 
  User, FileText, Stethoscope, ClipboardList, Heart,
  FileCheck, Brain, Sparkles, Search, UserPlus, UserCheck,
  Shield, Pill, Syringe, Activity, Clipboard, FileSignature,
  Home, CreditCard, Building, Users, ChevronDown, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive template categories with all document types
const TEMPLATE_CATEGORIES = [
  {
    category: 'Admission & Consent',
    icon: FileSignature,
    color: 'bg-blue-100 text-blue-700',
    templates: [
      { id: 'patient_facesheet', name: 'Patient Facesheet', description: 'Complete patient face sheet with demographics, insurance, contacts' },
      { id: 'admission_consent_exact', name: 'Admission Consent (IVORY form)', description: "Your agency's own consent form, word for word; boxes filled in" },
      { id: 'admission_packet_exact', name: 'Admission Packet HHA (IVORY form)', description: "Your agency's own 49-page packet, word for word; boxes filled in" },
      { id: 'emergency_info', name: 'Emergency Information', description: 'Client, doctor, allergies and emergency contact' },
      { id: 'billing_agreement', name: 'Billing Agreement', description: 'Responsible party and services' },
      { id: 'consent_verification', name: 'Consent Verification', description: 'Patient consent, HIPAA contacts and advance directives' },
      { id: 'home_assessment', name: 'Personal Care Home Assessment', description: 'Supports, physician, diagnosis and referral' },
    ]
  },
  {
    category: 'Medication Management',
    icon: Heart,
    color: 'bg-red-100 text-red-700',
    templates: [
      { id: 'medication_list', name: 'Medication List', description: 'Personal medication list with routine and PRN medications, dosages, frequencies' },
    ]
  },
  {
    category: 'OASIS Assessments',
    icon: FileText,
    color: 'bg-green-100 text-green-700',
    templates: [
      { id: 'soc_roc_assessment', name: 'SOC/ROC Assessment', description: 'Start of Care / Resumption of Care assessment' },
      { id: 'discharge_assessment', name: 'Discharge Assessment', description: 'Discharge assessment with outcomes' },
      { id: 'recertification_assessment', name: 'Recertification Assessment', description: 'Recertification period assessment' },
      { id: 'oasis_assessment', name: 'OASIS Assessment', description: 'Full OASIS-E assessment form' },
    ]
  },
  {
    category: 'Physician Orders (all order forms)',
    icon: FileSignature,
    color: 'bg-red-100 text-red-700',
    templates: [
      { id: 'doctors_orders', name: "Doctor's Order", description: 'Medications, treatments, labs, diet and activity' },
      { id: 'dme_order', name: 'Medical Supply / DME Order', description: 'Supplies and equipment, HCPCS, medical necessity' },
      { id: 'medication_order', name: 'Medication Order', description: 'New, changed or stopped medications with instructions' },
      { id: 'physician_orders', name: "Physician's Order", description: 'Verbal or written order with read-back and signature' },
      { id: 'therapy_order', name: 'Therapy Order', description: 'PT / OT / ST evaluate & treat, frequency, goals' },
      { id: 'wound_care_order', name: 'Wound Care Order', description: 'Wound, dressings, frequency and duration' },
    ]
  },
  {
    category: 'Clinical Documents',
    icon: Stethoscope,
    color: 'bg-purple-100 text-purple-700',
    templates: [
      { id: 'poc_485', name: '485 Plan of Care', description: 'CMS-485 Home Health Plan of Care' },
      { id: 'face_to_face', name: 'Face to Face Encounter', description: 'Face-to-face encounter documentation' },
    ]
  },
  {
    category: 'Nursing Documentation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700',
    templates: [
      { id: 'nurses_notes', name: 'Nurses Notes', description: 'Skilled nursing visit notes' },
      { id: 'hha_notes', name: 'Home Health Aide Notes', description: 'HHA visit documentation' },
      { id: 'lvn_notes', name: 'LVN Notes', description: 'Licensed Vocational Nurse notes' },
      { id: 'wound_care_flow', name: 'Wound Care Flow Sheet', description: 'Wound care tracking and documentation' },
      { id: 'wound_watfs', name: 'Wound Assessment & Treatment Flow Sheet (WATFS)', description: 'One per wound: stage, measurements, wound bed, treatment' },
      { id: 'iv_therapy', name: 'IV Therapy Administration', description: 'IV therapy administration records' },
    ]
  },
  {
    category: 'Therapy Documentation',
    icon: Activity,
    color: 'bg-amber-100 text-amber-700',
    templates: [
      { id: 'therapist_notes', name: 'Therapist Notes', description: 'PT/OT/ST therapy visit notes' },
      { id: 'pt_evaluation', name: 'Physical Therapy Initial Evaluation', description: 'PT evaluation with homebound reason, PLOF, gait and goals' },
      { id: 'ot_evaluation', name: 'Occupational Therapy Initial Evaluation', description: 'OT evaluation with ADLs/IADLs, UE function, home safety and goals' },
      { id: 'msw_evaluation', name: 'Social Work Evaluation', description: 'Living situation, support, resources, advance directives' },
      { id: 'dietitian_evaluation', name: 'Dietitian Evaluation', description: 'Weight, diet & intake, nutrition risks and needs' },
      { id: 'st_evaluation', name: 'Speech Therapy Initial Evaluation', description: 'ST evaluation with communication, cognition, swallowing and goals' },
    ]
  }
];
// titles and templates under each title are shown in alphabetical order
TEMPLATE_CATEGORIES.sort((a, b) => a.category.localeCompare(b.category));
TEMPLATE_CATEGORIES.forEach(c => c.templates.sort((a, b) => a.name.localeCompare(b.name)));

const GlobalUploadModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Upload, 2: Select Options, 3: Processing, 4: Results
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [result, setResult] = useState(null);
  
  // Patient selection
  const [patientMode, setPatientMode] = useState('new'); // 'new' or 'existing'
  const [existingPatients, setExistingPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  
  // Template selection
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Save/Cancel states
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Preview state
  const [previewingDoc, setPreviewingDoc] = useState(null);

  const getToken = useCallback(() => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  }, []);

  // Fetch existing patients when modal opens
  useEffect(() => {
    if (isOpen && patientMode === 'existing') {
      fetchPatients();
    }
  }, [isOpen, patientMode]);

  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.patients) {
        setExistingPatients(data.patients);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Filter patients by search query
  const filteredPatients = existingPatients.filter(p => {
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    return fullName.includes(patientSearchQuery.toLowerCase());
  });

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 15 * 1024 * 1024, // 15MB
  });

  // Handle select all templates
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTemplates([]);
    } else {
      const allTemplateIds = TEMPLATE_CATEGORIES.flatMap(cat => 
        cat.templates.map(t => t.id)
      );
      setSelectedTemplates(allTemplateIds);
    }
    setSelectAll(!selectAll);
  };

  // Handle individual template selection
  const handleTemplateToggle = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Handle category selection
  const handleCategoryToggle = (categoryTemplates) => {
    const categoryIds = categoryTemplates.map(t => t.id);
    const allSelected = categoryIds.every(id => selectedTemplates.includes(id));
    
    if (allSelected) {
      setSelectedTemplates(prev => prev.filter(id => !categoryIds.includes(id)));
    } else {
      setSelectedTemplates(prev => [...new Set([...prev, ...categoryIds])]);
    }
  };

  const handleNextStep = () => {
    if (step === 1 && selectedFile) {
      setStep(2);
    }
  };

  const handleUploadAndProcess = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template to populate');
      return;
    }

    if (patientMode === 'existing' && !selectedPatientId) {
      toast.error('Please select a patient');
      return;
    }

    setStep(3);
    setProcessing(true);
    setProcessingStep('Reading file...');

    try {
      // Convert file to base64
      const reader = new FileReader();
      const fileData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const base64Content = fileData.split(',')[1];

      setProcessingStep('Extracting comprehensive patient information with AI...');

      const token = getToken();
      const response = await fetch(`${API_URL}/api/ai/global-upload-comprehensive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_data: base64Content,
          file_type: selectedFile.type,
          file_name: selectedFile.name,
          patient_mode: patientMode,
          existing_patient_id: patientMode === 'existing' ? selectedPatientId : null,
          selected_templates: selectedTemplates
        })
      });

      const data = await response.json();

      if (data.success) {
        // Store result but mark as NOT saved yet - user must confirm
        setResult({ ...data, saved: false });
        setStep(4);
        setProcessingStep('');
        toast.info('Documents generated! Please review and click Save to confirm.');
      } else {
        // Show specific error message from backend
        const errorMsg = data.error || data.detail || data.message || 'Failed to process document';
        toast.error(errorMsg);
        setStep(2);
        setProcessingStep('');
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Handle non-JSON responses and network errors
      let errorMsg = 'Failed to upload and process document';
      if (error.message) {
        if (error.message.includes('budget') || error.message.includes('exceeded')) {
          errorMsg = 'AI budget exceeded. Please add balance via Profile → Universal Key → Add Balance.';
        } else if (error.message.includes('model')) {
          errorMsg = 'AI service temporarily unavailable. Please try again.';
        } else {
          errorMsg = error.message;
        }
      }
      toast.error(errorMsg);
      setStep(2);
      setProcessingStep('');
    } finally {
      setProcessing(false);
    }
  };

  // Handle SAVE - confirm and keep the documents
  const handleSaveUpload = async () => {
    setSaving(true);
    try {
      const token = getToken();
      // Call API to confirm/finalize the documents
      const response = await fetch(`${API_URL}/api/ai/confirm-upload/${result.patientId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Mark as saved
        setResult(prev => ({ ...prev, saved: true }));
        toast.success('Documents saved successfully!');
        
        // Store the populated sections info in localStorage
        localStorage.setItem('recentAutoPopulate', JSON.stringify({
          patientId: result.patientId,
          patientName: result.patientName,
          populatedSections: result.populatedSections,
          timestamp: new Date().toISOString()
        }));
        
        window.dispatchEvent(new CustomEvent('patientAutoPopulated', { detail: result }));
      } else {
        toast.error(data.error || data.detail || data.message || 'Failed to save documents');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save documents');
    } finally {
      setSaving(false);
    }
  };

  // Handle CANCEL - discard all generated documents
  const handleCancelUpload = async () => {
    setCancelling(true);
    try {
      const token = getToken();
      // Call API to delete the pending documents
      const response = await fetch(`${API_URL}/api/ai/cancel-upload/${result.patientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_mode: patientMode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Upload cancelled. Documents discarded.');
        resetModal();
        setStep(1);
      } else {
        toast.error(data.error || data.detail || data.message || 'Failed to cancel upload');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.message || 'Failed to cancel upload');
    } finally {
      setCancelling(false);
    }
  };

  const handleViewPatient = () => {
    if (result?.patientId) {
      onClose();
      navigate(`/dashboard/patients/demographics/${result.patientId}`);
    }
  };

  const handleViewSummary = () => {
    if (result?.patientId) {
      onClose();
      navigate(`/dashboard/auto-populate-summary/${result.patientId}`);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedFile(null);
    setResult(null);
    setSelectedTemplates([]);
    setSelectAll(false);
    setPatientMode('new');
    setSelectedPatientId('');
    setPatientSearchQuery('');
    setPreviewingDoc(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]" data-testid="global-upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#f39c12] to-[#e67e22] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  AI Document Generator
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                </h2>
                <p className="text-white/80 text-sm">
                  {step === 1 && 'Step 1: Upload Document'}
                  {step === 2 && 'Step 2: Select Options & Templates'}
                  {step === 3 && 'Step 3: Processing...'}
                  {step === 4 && 'Complete!'}
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-white text-[#f39c12]' :
                  'bg-white/30 text-white/60'
                }`}>
                  {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                {s < 4 && <div className={`w-12 h-1 rounded ${s < step ? 'bg-green-500' : 'bg-white/30'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Step 1: Upload */}
          {step === 1 && (
            <>
              <div 
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragActive 
                    ? 'border-[#f39c12] bg-orange-50' 
                    : selectedFile 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-[#f39c12] hover:bg-orange-50'
                }`}
                data-testid="upload-dropzone"
              >
                <input {...getInputProps()} data-testid="global-upload-input" />
                
                {selectedFile ? (
                  <div className="space-y-3">
                    <FileUp className="w-16 h-16 mx-auto text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700 text-lg">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      Remove & Choose Another
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-16 h-16 mx-auto text-gray-400" />
                    <div>
                      <p className="font-semibold text-gray-700 text-lg">
                        {isDragActive ? 'Drop your document here' : 'Upload Patient Document'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, images, or text files (max 15MB)
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Supports: discharge summaries, referrals, face sheets, handwritten notes, insurance cards
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={handleNextStep}
                  disabled={!selectedFile}
                  className="bg-[#f39c12] hover:bg-[#e67e22] text-white px-8"
                >
                  Next: Select Options
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Select Options & Templates */}
          {step === 2 && (
            <>
              {/* Patient Mode Selection */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#f39c12]" />
                  Patient Selection
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setPatientMode('new')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      patientMode === 'new' 
                        ? 'border-[#f39c12] bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="new-patient-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        patientMode === 'new' ? 'bg-[#f39c12] text-white' : 'bg-gray-100'
                      }`}>
                        <UserPlus className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Create New Patient</p>
                        <p className="text-sm text-gray-500">Generate all documents for a new patient</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setPatientMode('existing');
                      fetchPatients();
                    }}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      patientMode === 'existing' 
                        ? 'border-[#f39c12] bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="existing-patient-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        patientMode === 'existing' ? 'bg-[#f39c12] text-white' : 'bg-gray-100'
                      }`}>
                        <UserCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Update Existing Patient</p>
                        <p className="text-sm text-gray-500">Add documents to an existing patient</p>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Existing Patient Search */}
                {patientMode === 'existing' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <div className="relative mb-3">
                      <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search patients by name..."
                        value={patientSearchQuery}
                        onChange={(e) => setPatientSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f39c12]"
                        data-testid="patient-search-input"
                      />
                    </div>
                    
                    {loadingPatients ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-[#f39c12]" />
                      </div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {filteredPatients.slice(0, 10).map(patient => (
                          <button
                            key={patient.id}
                            onClick={() => setSelectedPatientId(patient.id)}
                            className={`w-full p-3 rounded-lg text-left transition-all flex items-center gap-3 ${
                              selectedPatientId === patient.id 
                                ? 'bg-[#f39c12] text-white' 
                                : 'bg-white hover:bg-gray-100'
                            }`}
                            data-testid={`patient-option-${patient.id}`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              selectedPatientId === patient.id ? 'bg-white/20' : 'bg-[#f39c12] text-white'
                            }`}>
                              {patient.firstName?.[0]}{patient.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium">{patient.firstName} {patient.lastName}</p>
                              <p className={`text-xs ${selectedPatientId === patient.id ? 'text-white/70' : 'text-gray-500'}`}>
                                DOB: {patient.dateOfBirth || 'N/A'}
                              </p>
                            </div>
                          </button>
                        ))}
                        {filteredPatients.length === 0 && (
                          <p className="text-center text-gray-500 py-4">No patients found</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Template Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#f39c12]" />
                    Documents to Generate
                  </h3>
                  <button
                    onClick={handleSelectAll}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectAll 
                        ? 'bg-[#f39c12] text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                    data-testid="select-all-templates"
                  >
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
                  {TEMPLATE_CATEGORIES.map(category => {
                    const Icon = category.icon;
                    const categoryIds = category.templates.map(t => t.id);
                    const allSelected = categoryIds.every(id => selectedTemplates.includes(id));
                    const someSelected = categoryIds.some(id => selectedTemplates.includes(id));
                    
                    return (
                      <div key={category.category} className="border rounded-xl overflow-hidden">
                        <button
                          onClick={() => handleCategoryToggle(category.templates)}
                          className={`w-full p-3 flex items-center justify-between ${category.color}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5" />
                            <span className="font-semibold">{category.category}</span>
                            <span className="text-sm opacity-70">
                              ({categoryIds.filter(id => selectedTemplates.includes(id)).length}/{category.templates.length})
                            </span>
                          </div>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            allSelected ? 'bg-green-500 border-green-500' : someSelected ? 'bg-yellow-500 border-yellow-500' : 'border-current'
                          }`}>
                            {allSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                            {someSelected && !allSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                        </button>
                        
                        <div className="p-2 bg-white space-y-1">
                          {category.templates.map(template => (
                            <label
                              key={template.id}
                              className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                selectedTemplates.includes(template.id) 
                                  ? 'bg-green-50 border border-green-200' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTemplates.includes(template.id)}
                                onChange={() => handleTemplateToggle(template.id)}
                                className="mt-1 w-4 h-4 text-[#f39c12] rounded focus:ring-[#f39c12]"
                                data-testid={`template-${template.id}`}
                              />
                              <div>
                                <p className="font-medium text-gray-800">{template.name}</p>
                                <p className="text-xs text-gray-500">{template.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold">Comprehensive AI Extraction</p>
                    <p className="mt-1">
                      The AI will extract and populate ALL available information including:
                      complete demographics, full address, insurance details (ID, group, payer),
                      diagnoses (ICD-10), medications, vital signs, physician info, and more.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleUploadAndProcess}
                  disabled={selectedTemplates.length === 0 || (patientMode === 'existing' && !selectedPatientId)}
                  className="flex-1 bg-[#f39c12] hover:bg-[#e67e22] text-white"
                  data-testid="generate-documents-btn"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Generate {selectedTemplates.length} Document{selectedTemplates.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Processing */}
          {step === 3 && (
            <div className="py-12 text-center">
              <div className="w-24 h-24 bg-[#f39c12]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-12 h-12 text-[#f39c12] animate-spin" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Your Document</h3>
              <p className="text-gray-600 mb-4">{processingStep}</p>
              
              <div className="max-w-md mx-auto space-y-2 text-left">
                {[
                  'Reading and analyzing document',
                  'Extracting patient demographics',
                  'Extracting insurance information',
                  'Extracting clinical data',
                  'Generating selected templates',
                  'Saving to database'
                ].map((stepText, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin text-[#f39c12]" />
                    {stepText}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Results - SAVE OR CANCEL */}
          {step === 4 && result && (
            <div className="space-y-6">
              {!result.saved ? (
                <>
                  {/* Pending Confirmation Header */}
                  <div className="text-center">
                    <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-10 h-10 text-yellow-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">Review Generated Documents</h3>
                    <p className="text-gray-600 mt-2">
                      {patientMode === 'new' ? 'New patient: ' : 'Updated patient: '}
                      <span className="font-semibold text-[#f39c12]">{result.patientName}</span>
                    </p>
                    <p className="text-sm text-blue-700 bg-blue-50 px-4 py-2 rounded-lg mt-3 inline-block">
                      Click on any document below to <strong>preview its content</strong>. Then Save or Cancel.
                    </p>
                  </div>

                  {/* Document Preview Section */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#f39c12]" />
                      Documents Ready to Save ({result.populatedSections?.length || 0}) - Click to Preview
                    </h4>
                    
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {result.populatedSections?.map(section => (
                        <div 
                          key={section.id}
                          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                        >
                          {/* Document Header - Clickable */}
                          <button
                            onClick={() => setPreviewingDoc(previewingDoc === section.id ? null : section.id)}
                            className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#f39c12]/10 rounded-lg flex items-center justify-center">
                                <FileText className="w-4 h-4 text-[#f39c12]" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-gray-800">{section.name}</p>
                                <p className="text-xs text-gray-500">{section.fieldsPopulated} fields populated</p>
                              </div>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                              previewingDoc === section.id ? 'rotate-180' : ''
                            }`} />
                          </button>
                          
                          {/* Document Preview Content - Expandable */}
                          {previewingDoc === section.id && section.data && (
                            <div className="border-t border-gray-200 p-4 bg-blue-50/50">
                              <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <Eye className="w-4 h-4" />
                                Document Preview - {section.name}
                              </h5>
                              <div className="bg-white rounded-lg border p-4 max-h-64 overflow-y-auto">
                                <table className="w-full text-sm">
                                  <tbody>
                                    {Object.entries(section.data).map(([key, value]) => {
                                      // Format the key to be readable
                                      const formattedKey = key
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/^./, str => str.toUpperCase())
                                        .trim();
                                      
                                      // Format the value
                                      let formattedValue = '';
                                      if (value === null || value === undefined) {
                                        formattedValue = <span className="text-gray-400 italic">Not provided</span>;
                                      } else if (typeof value === 'object') {
                                        if (Array.isArray(value)) {
                                          formattedValue = value.length > 0 
                                            ? value.map((v, i) => (
                                                <span key={i} className="inline-block bg-gray-100 px-2 py-0.5 rounded text-xs mr-1 mb-1">
                                                  {typeof v === 'object' ? JSON.stringify(v) : v}
                                                </span>
                                              ))
                                            : <span className="text-gray-400 italic">None</span>;
                                        } else {
                                          // Object like vitalSigns
                                          formattedValue = (
                                            <div className="space-y-1">
                                              {Object.entries(value).map(([k, v]) => (
                                                v && <div key={k} className="text-xs">
                                                  <span className="text-gray-500">{k}:</span> {v}
                                                </div>
                                              ))}
                                            </div>
                                          );
                                        }
                                      } else {
                                        formattedValue = String(value);
                                      }
                                      
                                      return (
                                        <tr key={key} className="border-b border-gray-100 last:border-0">
                                          <td className="py-2 pr-4 text-gray-600 font-medium w-1/3 align-top">
                                            {formattedKey}
                                          </td>
                                          <td className="py-2 text-gray-800">
                                            {formattedValue}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quick Extracted Data Summary */}
                  {result.extractedData && (
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        Patient Summary
                      </h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        {result.extractedData.firstName && (
                          <p><span className="text-gray-500">Name:</span> <strong>{result.extractedData.firstName} {result.extractedData.lastName}</strong></p>
                        )}
                        {result.extractedData.dateOfBirth && (
                          <p><span className="text-gray-500">DOB:</span> <strong>{result.extractedData.dateOfBirth}</strong></p>
                        )}
                        {result.extractedData.address && (
                          <p className="col-span-2"><span className="text-gray-500">Address:</span> <strong>{result.extractedData.address}, {result.extractedData.city}, {result.extractedData.state} {result.extractedData.zipCode}</strong></p>
                        )}
                        {result.extractedData.insurance && (
                          <p><span className="text-gray-500">Insurance:</span> <strong>{result.extractedData.insurance}</strong></p>
                        )}
                        {result.extractedData.insuranceMemberId && (
                          <p><span className="text-gray-500">Member ID:</span> <strong>{result.extractedData.insuranceMemberId}</strong></p>
                        )}
                        {result.extractedData.primaryDiagnosis && (
                          <p className="col-span-2"><span className="text-gray-500">Diagnosis:</span> <strong>{result.extractedData.primaryDiagnosis}</strong></p>
                        )}
                      </div>
                      
                      {/* Extracted Medications */}
                      {result.extractedData.currentMedications && result.extractedData.currentMedications.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-green-200">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Medications Extracted ({result.extractedData.currentMedications.length})</h5>
                          <div className="flex flex-wrap gap-1">
                            {(Array.isArray(result.extractedData.currentMedications) 
                              ? result.extractedData.currentMedications 
                              : [result.extractedData.currentMedications]
                            ).slice(0, 5).map((med, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {typeof med === 'object' ? med.name || med.medication : med}
                              </span>
                            ))}
                            {result.extractedData.currentMedications.length > 5 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{result.extractedData.currentMedications.length - 5} more
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Medications will be saved to patient profile automatically
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SAVE OR CANCEL Buttons */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      onClick={handleCancelUpload}
                      disabled={cancelling}
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                      data-testid="cancel-upload-btn"
                    >
                      {cancelling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Cancel & Discard
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={handleSaveUpload}
                      disabled={saving}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="save-upload-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Save Documents
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Saved Confirmation */}
                  <div className="text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">Documents Saved Successfully!</h3>
                    <p className="text-gray-600 mt-2">
                      {patientMode === 'new' ? 'New patient created: ' : 'Updated patient: '}
                      <span className="font-semibold text-[#f39c12]">{result.patientName}</span>
                    </p>
                  </div>

                  {/* Saved Documents Summary */}
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      Saved Documents ({result.populatedSections?.length || 0})
                    </h4>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {result.populatedSections?.map(section => (
                        <div 
                          key={section.id}
                          className="flex items-center gap-2 p-2 bg-white rounded-lg border border-green-200"
                        >
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">{section.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons After Save */}
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        resetModal();
                        setStep(1);
                      }}
                      className="flex-1"
                    >
                      Upload Another
                    </Button>
                    <Button 
                      onClick={handleViewSummary}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="view-summary-btn"
                    >
                      View Full Summary
                    </Button>
                    <Button 
                      onClick={handleViewPatient}
                      className="flex-1 bg-[#f39c12] hover:bg-[#e67e22] text-white"
                      data-testid="view-patient-btn"
                    >
                      Go to Patient
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalUploadModal;
